import type {
  AdditionalContextType,
  RawFlawedEventType,
  RawFlawedItemType,
} from '@vp/flawed-ux-tracking';
import { useCallback } from 'react';
import {
  ATC_WRAPPER_SERVICE_FLAWED_UX_REASON,
  FLAWED_ADD_TO_CART_EXPERIENCE,
  FLAWED_UX_SOURCE,
  MY_ACCOUNT_PAGE_SECTION,
  MY_ACCOUNT_PAGE_STAGE,
  ORDER_HISTORY_PAGE_NAME,
} from '../constants/tracking';
import { useSiteData } from './useSiteData';
import { trackFlawedUxEvent } from '../apis/trackingApi';
import { getQmReplayLink } from '../utils/quaantumMetricUtils';
import { useShopperId } from './useShopperId';
import { useLineItemData } from '../components/contexts/LineItemDataContext';
import { getReorderItemFailureReasons } from '../utils/reorder';

export const useFlawedAddToCartTracker = () => {
  const shopperId = useShopperId();
  const { siteName, vistacartTenant } = useSiteData();

  const {
    id: itemId,
    productKey,
    productVersion,
    quantity,
    attributes,
  } = useLineItemData();

  const trackAddToCartError = useCallback(
    (error: unknown) => {
      const additionalContext: AdditionalContextType = {};
      if (error instanceof Error) {
        additionalContext.apiErrorMessage = error.message;

        const responseStatus = (error as { response?: { status?: number } })
          .response?.status;
        if (responseStatus) {
          additionalContext.apiStatusCode = responseStatus;
        }
      }

      const itemData: RawFlawedItemType = {
        itemId: itemId || '',
        productKey: productKey || '',
        productVersion: productVersion || 0,
        quantity: quantity || 0,
        attributes: attributes || [],
        reasons: getReorderItemFailureReasons(error),
      };

      const eventData: RawFlawedEventType = {
        experience: FLAWED_ADD_TO_CART_EXPERIENCE,
        reasons: [ATC_WRAPPER_SERVICE_FLAWED_UX_REASON],
        source: FLAWED_UX_SOURCE,
        additionalContext,
        products: [productKey || ''],
        cartId: '',
        pageName: ORDER_HISTORY_PAGE_NAME,
        pageStage: MY_ACCOUNT_PAGE_STAGE,
        pageSection: MY_ACCOUNT_PAGE_SECTION,
        shopperId,
        site: siteName,
        tenant: vistacartTenant,
        impactedItems: [itemData],
        qmReplay: getQmReplayLink(),
        isApiFailure: true,
      };

      trackFlawedUxEvent(eventData);
    },
    [
      attributes,
      itemId,
      productKey,
      productVersion,
      quantity,
      shopperId,
      siteName,
      vistacartTenant,
    ]
  );

  return trackAddToCartError;
};
